'use client';

import { useEffect, useMemo, useState, useTransition } from 'react';
import type { FormEvent } from 'react';
import Link from 'next/link';
import { AlertCircle, MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthShell } from '@/components/layout/AuthShell';
import { PasswordRecoveryError, PasswordRecoveryService } from '@/services/passwordRecovery';

const RESEND_COOLDOWN_SECONDS = 30;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (!submittedAt) {
      return undefined;
    }

    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [submittedAt]);

  const cooldownRemaining = useMemo(() => {
    if (!submittedAt) {
      return 0;
    }

    const elapsedSeconds = Math.floor((now - submittedAt) / 1000);
    return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsedSeconds);
  }, [now, submittedAt]);

  const submitResetRequest = () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    setError(null);
    startTransition(async () => {
      try {
        await PasswordRecoveryService.requestReset({ email: normalizedEmail });
        setSubmittedEmail(normalizedEmail);
        setSubmittedAt(Date.now());
      } catch (err) {
        const message = err instanceof PasswordRecoveryError
          ? err.message
          : 'We could not process the password reset request right now.';
        setError(message);
      }
    });
  };

  const errorBanner = error ? (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{error}</span>
    </div>
  ) : null;

  if (submittedAt) {
    return (
      <AuthShell
        title="Check Your Email"
        description="If an active account exists for this email, we sent a password reset link."
        footer={
          <>
            Remembered your password?{' '}
            <Link href="/login" className="underline font-semibold" prefetch={false}>
              Back to login
            </Link>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <MailCheck className="h-5 w-5" />
            <span className="text-sm">{submittedEmail}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            For security, this screen looks the same whether the account exists or not.
          </p>
          <p className="text-sm text-muted-foreground">
            {cooldownRemaining > 0
              ? `You can request another link in ${cooldownRemaining}s.`
              : 'You can request another link if you still need help.'}
          </p>
          {errorBanner}
          <Button
            type="button"
            className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold"
            disabled={isPending || cooldownRemaining > 0}
            onClick={submitResetRequest}
          >
            {isPending ? 'Sending...' : 'Send Another Link'}
          </Button>
          <Button type="button" variant="ghost" className="w-full" asChild>
            <Link href="/login" prefetch={false}>Back to login</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot Your Password?"
      description="Enter your email and we will send a one-time reset link."
      footer={
        <>
          Remembered your password?{' '}
          <Link href="/login" className="underline font-semibold" prefetch={false}>
            Back to login
          </Link>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          submitResetRequest();
        }}
        noValidate
      >
        <div className="grid gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="m@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </div>
        {errorBanner}
        <Button
          type="submit"
          className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold"
          disabled={isPending}
        >
          {isPending ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </form>
    </AuthShell>
  );
}
